import type { GameParameters as GameParametersValues } from '../../../../services/games';
import type { GameLanguage } from '../../../../services/gameLanguages';
import { preferLocalized } from '../../../../helpers/commonUtils';
import type {
    BrowserIconKey,
    ParameterRow,
    ParameterTextRow,
    PlatformIconKey,
} from './types';

function parseGridDimensions(
    grid: string | null | undefined
): { columns: string; rows: string } {
    if (!grid?.trim()) {
        return { columns: '', rows: '' };
    }

    const match = grid.trim().match(/^(\d+)\s*[x×X]\s*(\d+)$/);
    if (!match) {
        return { columns: '', rows: '' };
    }

    return { columns: match[1], rows: match[2] };
}

function normalizeIconKey(value: string): string {
    return value.trim().toLowerCase();
}

const PLATFORM_ICON_KEYS = new Set<string>(['desktop', 'android', 'apple']);
const BROWSER_ICON_KEYS = new Set<string>([
    'chrome',
    'firefox',
    'edge',
    'safari',
    'opera',
]);

function filterPlatformKeys(values: string[]): PlatformIconKey[] {
    return values
        .map(normalizeIconKey)
        .filter((value): value is PlatformIconKey => PLATFORM_ICON_KEYS.has(value));
}

function filterBrowserKeys(values: string[]): BrowserIconKey[] {
    return values
        .map(normalizeIconKey)
        .filter((value): value is BrowserIconKey => BROWSER_ICON_KEYS.has(value));
}

export function buildParameterRows(
    parameters: GameParametersValues,
    gameLanguage: GameLanguage | null
): ParameterRow[] {
    const gridDimensions = parseGridDimensions(parameters.grid);
    const platformValues = filterPlatformKeys(parameters.supportedPlatforms ?? []);
    const browserValues = filterBrowserKeys(parameters.supportedBrowsers ?? []);

    const textRows = [
        {
            kind: 'text' as const,
            label: 'Category',
            value: preferLocalized(gameLanguage?.category, parameters.category),
        },
        {
            kind: 'text' as const,
            label: 'Release Date',
            value: parameters.releaseDate?.trim() || null,
        },
        {
            kind: 'text' as const,
            label: 'Max Win',
            value: parameters.maxWin?.trim() || null,
        },
        {
            kind: 'text' as const,
            label: 'Bet',
            value: preferLocalized(gameLanguage?.bet, parameters.bet),
        },
        {
            kind: 'text' as const,
            label: 'Reels',
            value: parameters.reels?.trim() || gridDimensions.columns || null,
        },
        {
            kind: 'text' as const,
            label: 'Rows',
            value: parameters.rows?.trim() || gridDimensions.rows || null,
        },
        {
            kind: 'text' as const,
            label: 'Mechanic',
            value: preferLocalized(gameLanguage?.mechanic, parameters.mechanic),
        },
        {
            kind: 'text' as const,
            label: 'Mobile',
            value: parameters.mobile?.trim() || null,
        },
    ].filter((row): row is ParameterTextRow => Boolean(row.value));

    const rows: ParameterRow[] = [...textRows];

    if (platformValues.length) {
        rows.push({
            kind: 'platforms',
            label: 'Supported Platforms',
            values: platformValues,
        });
    }

    if (browserValues.length) {
        rows.push({
            kind: 'browsers',
            label: 'Supported Browsers',
            values: browserValues,
        });
    }

    return rows;
}
